import BigNumber from 'bignumber.js'

export const ROUNDING_MODES = {
  ROUND_UP: 0,
  ROUND_DOWN: 1,
  ROUND_CEIL: 2,
  ROUND_FLOOR: 3,
  ROUND_HALF_UP: 4,
  ROUND_HALF_DOWN: 5,
  ROUND_HALF_EVEN: 6,
  ROUND_HALF_CEIL: 7,
  ROUND_HALF_FLOOR: 8,
} as const

export type RoundingMode = (typeof ROUNDING_MODES)[keyof typeof ROUNDING_MODES]

const ONE = 1000000
const DECIMAL_PLACES = 6
const MAX_ALLOWED_PERCENT = 100

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class MathUtil {
  static multiply(
    a: string | number,
    b: string | number,
    roundType: RoundingMode = ROUNDING_MODES.ROUND_HALF_UP
  ): string {
    if (!this._isValidParams(a, b)) return '0'

    BigNumber.config({ ROUNDING_MODE: roundType })
    BigNumber.config({ DECIMAL_PLACES: 0 })

    const mul1 = new BigNumber(new BigNumber(a).times(new BigNumber(ONE)))
    const mul2 = new BigNumber(new BigNumber(b).times(new BigNumber(ONE)))
    const result = mul1.times(mul2)

    BigNumber.config({ DECIMAL_PLACES })

    return result
      .dividedBy(new BigNumber(ONE))
      .dividedBy(new BigNumber(ONE))
      .toFixed(DECIMAL_PLACES)
  }

  static divide(
    a: string | number,
    b: string | number,
    roundType: RoundingMode = ROUNDING_MODES.ROUND_HALF_UP
  ): string {
    if (!this._isValidParams(a, b)) return '0'

    BigNumber.config({ ROUNDING_MODE: roundType })
    BigNumber.config({ DECIMAL_PLACES })

    const num = new BigNumber(new BigNumber(a).times(new BigNumber(ONE)))
    const denum = new BigNumber(new BigNumber(b).times(new BigNumber(ONE)))

    const result = num.dividedBy(denum)

    return result.toFixed(DECIMAL_PLACES)
  }

  static subtract(
    a: string | number,
    b: string | number,
    roundType: RoundingMode = ROUNDING_MODES.ROUND_HALF_UP
  ): string {
    if (!this._isValidParams(a, b)) return '0'

    BigNumber.config({ ROUNDING_MODE: roundType })
    BigNumber.config({ DECIMAL_PLACES })

    const one = new BigNumber(a)
    const two = new BigNumber(b)

    const result = one.minus(two)
    return result.toFixed(DECIMAL_PLACES)
  }

  static add(
    a: string | number,
    b: string | number,
    roundType: RoundingMode = ROUNDING_MODES.ROUND_HALF_UP
  ): string {
    if (!this._isValidParams(a, b)) return '0'

    BigNumber.config({ ROUNDING_MODE: roundType })
    BigNumber.config({ DECIMAL_PLACES })

    const one = new BigNumber(a)
    const two = new BigNumber(b)

    const result = one.plus(two)
    return result.toFixed(DECIMAL_PLACES)
  }

  /**
   * Compare two values
   * @returns 1 if a > b, -1 if a < b, 0 if a === b
   */
  static compare(a: string | number, b: string | number): number {
    if (!this._isValidParams(a, b)) return -1

    const one = new BigNumber(a)
    const two = new BigNumber(b)

    const result = one.comparedTo(two)
    return result === null ? -1 : result
  }

  /**
   * Format value with config
   */
  static format(
    value: string | number,
    config: {
      decimalPlaces: number
      prefix?: string
      suffix?: string
      decimalSeparator?: string
      groupSeparator?: string
      groupSize?: number
      secondaryGroupSize?: number
      fractionGroupSeparator?: string
      fractionGroupSize?: number
    }
  ): string {
    BigNumber.config({ FORMAT: config })

    const num = new BigNumber(value)
    const result = new BigNumber(
      num.toFixed(config.decimalPlaces, ROUNDING_MODES.ROUND_HALF_UP)
    )

    return result.toFormat()
  }

  /**
   * Calculate percentage of a value
   * Example: value = 200, percent = 15. Return - 30
   */
  static percentOfValue(
    value: string | number,
    percent: string | number,
    roundType: RoundingMode = ROUNDING_MODES.ROUND_HALF_UP
  ): string {
    const percentValue = MathUtil.divide(percent, MAX_ALLOWED_PERCENT, roundType)
    return MathUtil.multiply(value, percentValue, roundType)
  }

  /**
   * Round to specified decimal places using ROUND_HALF_UP (mathematical rounding)
   */
  static roundTo(value: string | number, decimalPlaces: number): string {
    if (!this._isValidParams(value, 0)) return '0'

    BigNumber.config({ ROUNDING_MODE: ROUNDING_MODES.ROUND_HALF_UP })
    const num = new BigNumber(value)
    return num.toFixed(decimalPlaces)
  }

  /**
   * Round up to specified decimal places (ceiling)
   */
  static roundUpTo(value: string | number, decimalPlaces: number): string {
    if (!this._isValidParams(value, 0)) return '0'

    BigNumber.config({ ROUNDING_MODE: ROUNDING_MODES.ROUND_CEIL })
    const num = new BigNumber(value)
    return num.toFixed(decimalPlaces)
  }

  /**
   * Convert coins (USDT) to oz gold using XAUT price
   * Formula: coins / xautPriceInUSD = oz gold
   * Since 1 XAUT = 1 troy oz of gold
   */
  static coinsToOzGold(coins: string | number, xautPriceUSD: number): string {
    if (!this._isValidParams(coins, xautPriceUSD)) return '0'
    if (xautPriceUSD <= 0) return '0'

    BigNumber.config({ ROUNDING_MODE: ROUNDING_MODES.ROUND_HALF_UP })

    const coinsValue = new BigNumber(coins)
    const price = new BigNumber(xautPriceUSD)
    const ozGold = coinsValue.dividedBy(price)

    // Round up to hundredths as specified
    return ozGold.toFixed(2, ROUNDING_MODES.ROUND_CEIL)
  }

  /**
   * Format coins with thousand separators
   */
  static formatCoins(value: string | number, decimalPlaces: number = 2): string {
    if (!this._isValidParams(value, 0)) return '0.00'

    const num = new BigNumber(value)

    return num.toFormat(decimalPlaces, ROUNDING_MODES.ROUND_HALF_UP, {
      decimalSeparator: '.',
      groupSeparator: ',',
      groupSize: 3,
    })
  }

  /**
   * Truncate a multiplier to 2 decimal places (floor, not round).
   * Uses BigNumber to avoid IEEE 754 precision errors
   * (Math.floor(2.3 * 100) gives 229, not 230).
   * Example: 2.349 → 2.34, 2.999 → 2.99, 2.3 → 2.3
   */
  static truncateMultiplier(value: number): number {
    if (!Number.isFinite(value)) return 0
    return new BigNumber(value).decimalPlaces(2, ROUNDING_MODES.ROUND_DOWN).toNumber()
  }

  static round(n: number): number {
    return Math.round(n)
  }

  static get roundingModes(): typeof ROUNDING_MODES {
    return ROUNDING_MODES
  }

  private static _isValidParams(
    a: string | number,
    b: string | number,
    c: string | number = 0
  ): boolean {
    try {
      new BigNumber(a)
      new BigNumber(b)
      new BigNumber(c)
      return true
    } catch {
      return false
    }
  }
}
